import React, { useEffect, useCallback } from 'react';
import type { Node } from 'react';
import ReactModal from 'react-modal';
import styled from '@emotion/styled';
import { css, Global, useTheme } from '@emotion/react';
import { useInView } from 'react-intersection-observer';
import { getAppType } from '@vmos2/core';
import { isAppleDevice } from '@vmos2/utils';
import { useAppType, useDeviceSize, useAccessibilityContext } from '@vmos2/providers';

import { Footer } from '../footer/Footer';
import { useComponentTheme } from '../useComponentTheme';

import { ModalHeader } from './components/ModalHeader';

type Props = {
  children: Node | Node[],
  isOpen: boolean,
  hideHeader?: boolean,
  contentLabel?: string,
  closeTimeoutMS?: number,
  onAfterOpen?: () => void,
  onAfterRequestClose?: Function,
  onRequestClose: Function,
  className: ?string,
  dataTest?: string,
  size: 'large' | 'small' | 'full-height' | 'full-width',
  title?: string,
  titleIcon?: Node | string,
  titleId?: string,
  titleCentered?: boolean,
  footerSections: number,
  footerRightSide?: Node,
  footerLeftSide?: Node,
  footerIgnoreThemeHeight?: boolean,
  isFullScreen?: boolean,
  height?: string,
  isSticky?: boolean,
  shouldCloseOnOverlayClick?: boolean,
  showLeftSideMobile?: boolean,
  hasCloseButton?: boolean,
  hasWrapperMargin?: boolean,
  leftSide?: Function,
  rightSide?: Function,
  stickToBottom?: Boolean,
  stickToBottomOnMobile?: boolean,
  mode?: 'QRScan',
  contentAutoHeight?: boolean,
  progressValue?: number,
  onBackButtonClick?: Function,
  disableTransitions?: boolean,
  extraComponent?: Node,
  reverseFooterOrder?: boolean,
};

export const Modal = ({
  className = null,
  children,
  dataTest,
  isOpen,
  hideHeader,
  hasCloseButton = true,
  hasWrapperMargin = true,
  contentLabel,
  onAfterOpen,
  onAfterRequestClose,
  onRequestClose,
  closeTimeoutMS = 250,
  title = '',
  titleIcon,
  titleId,
  size = 'large',
  footerSections = 2,
  footerLeftSide,
  footerRightSide,
  footerIgnoreThemeHeight,
  isFullScreen,
  height,
  isSticky,
  shouldCloseOnOverlayClick = true,
  showLeftSideMobile,
  titleCentered = false,
  leftSide,
  rightSide,
  stickToBottom,
  stickToBottomOnMobile,
  mode,
  contentAutoHeight,
  progressValue,
  onBackButtonClick,
  disableTransitions = false,
  extraComponent,
  reverseFooterOrder = false,
}: Props): Node => {
  const { isLarge, isMobile } = useDeviceSize();
  const theme = useTheme();
  const componentTheme = useComponentTheme(theme => theme.components.Modal, mode);
  const [ref, headerInView] = useInView();
  const { isKiosk } = useAppType();
  const { a11yMode } = useAccessibilityContext();
  const closeTimeoutSeconds = `${closeTimeoutMS / 1000}s`;
  const hasSideContent = leftSide || rightSide;
  const modalLabel = contentLabel || title || undefined;

  const modalWidth = (() => {
    if (size === 'full-width' || isFullScreen || (isMobile && stickToBottomOnMobile)) {
      return '100%';
    }

    if (isLarge) {
      if (size === 'large') {
        return '880px';
      }

      return '550px';
    }

    return '90%';
  })();

  const style = {
    overlay: {
      backgroundColor: `rgba(0, 0, 0, ${hasSideContent ? 0.8 : 0.5})`,
      backgroundImage: componentTheme.overlayBackgroundImage
        ? `url('${componentTheme.overlayBackgroundImage}')`
        : 'none',
      zIndex: theme.positioning.modal,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: isKiosk && isAppleDevice() && !hasSideContent ? 'flex-start' : 'center',
      alignItems: 'center',
      paddingTop: 'env(safe-area-inset-top)',
      ...(disableTransitions && { transition: 'none' }),
    },
    content: {
      borderRadius: componentTheme.borderRadius || 0,
      ...(stickToBottom && { marginTop: 'auto' }),
      ...(disableTransitions && { transition: 'none' }),
    },
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const afterOpen = () => {
    if (onAfterOpen) {
      window.setTimeout(() => {
        onAfterOpen();
      }, closeTimeoutMS);
    }
  };

  const handleClose = useCallback(() => {
    if (onBackButtonClick) {
      return;
    }

    document.body.style.overflow = '';
    onRequestClose();

    if (onAfterRequestClose) {
      window.setTimeout(() => {
        onAfterRequestClose();
      }, closeTimeoutMS);
    }
  }, [closeTimeoutMS, onAfterRequestClose, onBackButtonClick, onRequestClose]);

  return (
    <>
      <Global
        styles={css`
          .ReactModal__Overlay {
            opacity: 0;
            transition: opacity ${closeTimeoutSeconds} ease-in-out;
          }

          .ReactModal__Overlay--after-open {
            opacity: 1;
          }

          .ReactModal__Overlay.is-closing {
            opacity: 0;
            position: fixed;
            top: 0px;
            left: 0px;
            right: 0px;
            bottom: 0px;
          }

          .ReactModal__Content.is-closing,
          .ReactModal__Content--before-close {
            opacity: 0;
            bottom: -250px !important;
          }

          .opacity-transition {
            opacity: 0;
            bottom: -250px;
            transition: opacity ${closeTimeoutSeconds} ease-in-out, bottom ${closeTimeoutSeconds} ease-in-out;
          }

          .ReactModal__Content--after-open {
            opacity: 1;
            bottom: 0;
          }
        `}
      />

      <StyledModal
        a11yMode={a11yMode}
        appElement={document.getElementById('single-spa-application-root')}
        className={getAppType() !== 'kiosk' && 'opacity-transition'}
        closeTimeoutMS={closeTimeoutMS}
        contentLabel={modalLabel}
        data={{ test: dataTest || 'modal' }}
        hasSideContent={hasSideContent}
        height={height}
        isAppleDevice={isAppleDevice()}
        isFullScreen={isFullScreen}
        isOpen={isOpen}
        onAfterOpen={afterOpen}
        onRequestClose={handleClose}
        overlayClassName={!isOpen ? 'is-closing' : ''}
        shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
        size={size}
        stickToBottomOnMobile={stickToBottomOnMobile}
        style={style}
        width={modalWidth}
      >
        {!hideHeader && (
          <ModalHeader
            isFullScreen={isFullScreen}
            isSticky={isSticky}
            handleClose={handleClose}
            hasCloseButton={hasCloseButton}
            title={title}
            // The modal is labelled with this title, which is read out by screen readers when the modal is opened.
            // To prevent the screen reader from reading out the title again when navigating through the DOM, we have
            // added aria-hidden="true" to it.
            titleAriaHidden={!!modalLabel}
            titleIcon={titleIcon}
            titleId={titleId}
            titleCentered={titleCentered}
            headerInView={headerInView}
            progressValue={progressValue}
            onBackButtonClick={onBackButtonClick}
            mode={mode}
          />
        )}

        <Content
          className={className}
          data-test="modal-content"
          isFullScreen={isFullScreen}
          contentAutoHeight={contentAutoHeight}
        >
          <Wrapper
            data-test="modal-content-wrapper"
            hasWrapperMargin={hasWrapperMargin}
            height={height}
            hideHeader={hideHeader}
            isFullScreen={isFullScreen}
            size={size}
          >
            <Separator ref={ref} aria-hidden="true" />
            {children}
          </Wrapper>
        </Content>

        {footerRightSide && (
          <Footer
            showLeftSideMobile={showLeftSideMobile}
            ignoreThemeHeight={footerIgnoreThemeHeight}
            sections={footerSections}
            leftSide={footerLeftSide}
            rightSide={footerRightSide}
            dataTest="modal-footer"
            reverseOrder={reverseFooterOrder}
          />
        )}

        {leftSide && (
          <SideWrapperLeft a11yMode={a11yMode} data-test="modal-content-left-side" modalWidth={modalWidth}>
            {leftSide}
          </SideWrapperLeft>
        )}

        {rightSide && (
          <SideWrapperRight a11yMode={a11yMode} data-test="modal-content-right-side" modalWidth={modalWidth}>
            {rightSide}
          </SideWrapperRight>
        )}
      </StyledModal>

      {isOpen && !a11yMode && extraComponent && (
        <ExtraComponent componentTheme={componentTheme}>{extraComponent}</ExtraComponent>
      )}
    </>
  );
};

const SideContent = styled('div')(
  ({ a11yMode, modalWidth }) => `
  position: fixed;
  top: ${a11yMode ? '75%' : '50%'};
  width: calc((100% - ${modalWidth}) / 2);
  transform: translateY(-50%);
`
);

const SideWrapperLeft = styled(SideContent)`
  left: 0;
`;

const SideWrapperRight = styled(SideContent)`
  right: 0;
`;

const StyledModal = styled(ReactModal)(
  ({ hasSideContent, isAppleDevice, isFullScreen, height, size, stickToBottomOnMobile, theme, width, a11yMode }) => `
  background-color: ${theme.color.white};
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  position: relative;
  width: ${width};
  ${height && `height: ${height}`};

  ${
    isFullScreen
      ? `height: 100%;`
      : `
        max-height: 96%;
        border-radius: ${theme.borderRadius.large};
        overflow: hidden;
      `
  };

  :focus {
    outline: none;
  }

  @media (${theme.breakpoint.desktop}) {
    ${
      !isFullScreen && size === 'full-height'
        ? 'max-height: calc(100% - 80px)'
        : !isFullScreen
        ? 'max-height: 640px'
        : ''
    };
    ${!a11yMode && `margin: 0`};
    ${!a11yMode && isAppleDevice && !hasSideContent ? `margin-top: 32px;` : ''}
  }

  @media (${theme.breakpoint.largeScreenKiosk}) {
    max-height: inherit;
  }

  @media (${theme.breakpoint.mobile}) {
    ${!a11yMode && stickToBottomOnMobile && `margin-top: auto;`};
  }

  ${
    !!a11yMode &&
    `
      max-height: calc(${a11yMode} - ${theme.margins.xxl} * 2) !important;
      margin-top: ${a11yMode};
    `
  };
`
);

const Content = styled.div`
  flex: 1 1 auto;
  display: flex;
  width: 100%;
  padding: ${({ isFullScreen }) => !isFullScreen && '0 24px'};
  overflow-y: auto;
  ${({ contentAutoHeight = false }) => !contentAutoHeight && 'height: 100%;'};
  max-height: ${({ isFullScreen }) => !isFullScreen && '640px'};

  ::-webkit-scrollbar {
    width: 0 !important;
  }

  @media (${({ theme }) => theme.breakpoint.desktop}) {
    padding: 0 32px;
    max-height: inherit;
  }

  @media (${({ theme }) => theme.breakpoint.largeScreenKiosk}) {
    max-height: inherit;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  min-height: 28px;
  height: ${({ height }) => (height ? '100%' : 'fit-content')};
  margin-top: ${({ hasWrapperMargin, height, hideHeader, isFullScreen }) =>
    hideHeader && hasWrapperMargin && !isFullScreen && !height && '24px'};
  margin-left: ${({ hasWrapperMargin, isFullScreen }) => (hasWrapperMargin && isFullScreen ? '24px' : '0')};
  margin-right: ${({ hasWrapperMargin, isFullScreen }) => (hasWrapperMargin && isFullScreen ? '24px' : '0')};
  margin-bottom: ${({ hasWrapperMargin, isFullScreen, height }) =>
    hasWrapperMargin && !isFullScreen && !height && '24px'};

  @media (${({ theme }) => theme.breakpoint.desktop}) {
    ${({ hasWrapperMargin, height, hideHeader }) => hideHeader && hasWrapperMargin && !height && 'margin-top: 32px'};
    ${({ hasWrapperMargin, height }) => hasWrapperMargin && !height && 'margin-bottom: 32px'};
    ${({ size }) => (size !== 'full-height' ? 'overflow: hidden;' : '')}
  }
`;

const Separator = styled('hr')`
  height: 1px;
  border: none;
  margin: 0 0 -1px;
`;

const ExtraComponent = styled('div')`
  position: fixed;
  bottom: ${({ componentTheme }) => componentTheme.ExtraComponent.bottom};
  right: ${({ componentTheme }) => componentTheme.ExtraComponent.right};
  top: ${({ componentTheme }) => componentTheme.ExtraComponent.top};
  left: ${({ componentTheme }) => componentTheme.ExtraComponent.left};
  padding: ${({ componentTheme }) => componentTheme.ExtraComponent.padding};
  background-color: ${({ theme, componentTheme }) =>
    componentTheme.ExtraComponent.backgroundColor || theme.color.white};
  z-index: ${({ theme }) => theme.positioning.modal + 1};
  border-radius: ${({ componentTheme }) => componentTheme.ExtraComponent.borderRadius || componentTheme.borderRadius};
`;
